import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Trophy, ShieldCheck, Clock, Gamepad2 } from "lucide-react";
import api, { formatIDR } from "@/lib/api";
import AdSlot from "@/components/AdSlot";

export default function LandingPage() {
  const [units, setUnits] = useState([]);
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/units").then((r) => setUnits(r.data));
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3)));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  return (
    <div className="pt-20" data-testid="landing-page">
      {/* Header Ad Banner */}
      {settings && settings.ad_slot_header && settings.ad_header_enabled !== false && (
        <div className="border-b border-white/5 bg-[#0A0A0B] py-4 px-6" data-testid="ad-header-container">
          <div className="max-w-7xl mx-auto">
            <AdSlot
              snippet={settings.ad_slot_header}
              publisherId={settings.adsense_publisher_id}
              label="Sponsored"
              slotKey="header"
            />
          </div>
        </div>
      )}
      {/* HERO */}
      <section className="relative overflow-hidden bg-glow-cyan">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 lg:col-start-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00E5FF]/40 text-[#00E5FF] text-xs uppercase tracking-widest mb-8">
                <Zap size={12} /> Booking Online 24/7
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-[0.95] tracking-tighter">
                LEVEL UP<br />
                YOUR GAME <span className="text-[#00E5FF]">SESSION</span>.
              </h1>
              <p className="mt-8 text-lg text-gray-400 max-w-xl leading-relaxed">
                Rental PlayStation dengan slot booking online, turnamen berhadiah, dan setup gaming terbaik di kota.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/booking"
                  data-testid="hero-book-btn"
                  className="group inline-flex items-center gap-3 bg-[#00E5FF] text-black font-bold px-8 py-4 uppercase tracking-wider text-sm hover:bg-white transition-colors duration-200 neon-glow"
                >
                  Booking Sekarang <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/events"
                  data-testid="hero-events-btn"
                  className="inline-flex items-center gap-3 border border-white/20 text-white px-8 py-4 uppercase tracking-wider text-sm font-semibold hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"
                >
                  Lihat Turnamen
                </Link>
              </div>

              <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
                {[
                  { n: "50+", l: "Sesi/Hari" },
                  { n: "12", l: "Unit PS" },
                  { n: "7", l: "Turnamen" },
                ].map((s) => (
                  <div key={s.l} className="border-l-2 border-[#00E5FF] pl-4">
                    <div className="font-heading text-3xl font-black text-white">{s.n}</div>
                    <div className="text-xs uppercase tracking-widest text-gray-500 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-white/10 bg-[#0F0F11]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { i: Clock, t: "Slot Real-Time", d: "Cek ketersediaan slot langsung." },
            { i: ShieldCheck, t: "Bayar QRIS/E-Wallet", d: "Upload bukti, konfirmasi cepat." },
            { i: Trophy, t: "Turnamen Rutin", d: "Ikut kompetisi, menang hadiah." },
            { i: Zap, t: "Notif WhatsApp", d: "Konfirmasi booking langsung ke WA." },
          ].map((f, i) => (
            <motion.div
              key={f.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="p-6 border border-white/5 hover:border-[#00E5FF]/40 transition-colors bg-[#0A0A0B]"
            >
              <f.i size={24} className="text-[#00E5FF]" />
              <div className="font-heading font-bold text-white mt-4">{f.t}</div>
              <div className="text-sm text-gray-400 mt-2 leading-relaxed">{f.d}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Units */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="units-section">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-3">Katalog Unit</div>
            <h2 className="font-heading text-3xl md:text-5xl font-black text-white tracking-tighter">
              PILIH KONSOL,<br />MULAI PETUALANGAN.
            </h2>
          </div>
          <Link to="/booking" className="text-sm uppercase tracking-widest text-white hover:text-[#00E5FF] flex items-center gap-2">
            Semua Unit <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {units.map((u, i) => (
            <motion.div
              key={u.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              data-testid={`unit-card-${u.id}`}
              className="group border border-white/10 hover:border-[#00E5FF]/50 bg-[#0F0F11] overflow-hidden transition-colors"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={u.image_url || "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?w=800&q=80"}
                  alt={u.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                <div className="absolute top-4 left-4 px-2 py-1 bg-[#00E5FF] text-black text-xs font-black uppercase tracking-wider">
                  {u.console_type}
                </div>
              </div>
              <div className="p-6">
                <div className="font-heading text-xl font-bold text-white">{u.name}</div>
                <p className="text-sm text-gray-400 mt-2 line-clamp-2 leading-relaxed">{u.description}</p>
                <div className="mt-5 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider">Harga</div>
                    <div className="font-heading text-lg font-black text-[#00E5FF]">{formatIDR(u.price_per_hour)}/jam</div>
                  </div>
                  <Link
                    to={`/booking?unit=${u.id}`}
                    data-testid={`book-unit-${u.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black text-xs uppercase tracking-wider font-bold hover:bg-[#00E5FF] transition-colors"
                  >
                    Book <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Catalog In-Feed Ad */}
        {settings && settings.ad_slot_catalog && settings.ad_catalog_enabled !== false && (
          <div className="mt-10 py-6 border-y border-white/5" data-testid="ad-catalog-container">
            <AdSlot
              snippet={settings.ad_slot_catalog}
              publisherId={settings.adsense_publisher_id}
              label="Sponsored"
              slotKey="catalog"
            />
          </div>
        )}
      </section>

      {/* Events preview */}
      {events.length > 0 && (
        <section className="border-t border-white/10 bg-[#0F0F11]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#FF0055] mb-3">Live & Upcoming</div>
                <h2 className="font-heading text-3xl md:text-5xl font-black text-white tracking-tighter">
                  ARENA<br />TURNAMEN.
                </h2>
              </div>
              <Link to="/events" className="text-sm uppercase tracking-widest text-white hover:text-[#FF0055] flex items-center gap-2">
                Lihat Semua <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {events.map((e) => (
                <div key={e.id} className="border border-white/10 bg-[#0A0A0B] p-6 hover:border-[#FF0055]/40 transition-colors">
                  <div className={`inline-block px-2 py-1 text-[10px] font-black uppercase tracking-widest ${
                    e.status === "ongoing" ? "bg-[#FF0055] text-white" : e.status === "upcoming" ? "bg-[#00E5FF] text-black" : "bg-gray-700 text-white"
                  }`}>
                    {e.status === "ongoing" ? "● LIVE" : e.status.toUpperCase()}
                  </div>
                  <div className="font-heading text-xl font-bold text-white mt-4">{e.title}</div>
                  <div className="text-sm text-gray-400 mt-1">{e.game}</div>
                  <div className="mt-4 pt-4 border-t border-white/5 flex justify-between text-xs">
                    <div>
                      <div className="text-gray-500 uppercase tracking-wider">Prize</div>
                      <div className="text-white font-bold mt-1">{e.prize_pool || "-"}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-500 uppercase tracking-wider">Tanggal</div>
                      <div className="text-white font-bold mt-1">{e.event_date || "-"}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="relative overflow-hidden border border-[#00E5FF]/30 p-10 md:p-16">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/5 via-transparent to-[#7000FF]/10" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <Gamepad2 size={40} className="text-[#00E5FF]" />
              <h3 className="font-heading text-3xl md:text-4xl font-black text-white mt-4 tracking-tighter">
                SIAP JADI CHAMPION?
              </h3>
              <p className="text-gray-400 mt-4 leading-relaxed">
                Amankan slotmu sekarang. Bayar via QRIS/E-Wallet, upload bukti, dan tinggal main.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Link
                to="/booking"
                data-testid="cta-book-btn"
                className="inline-flex items-center gap-3 bg-[#00E5FF] text-black font-bold px-10 py-5 uppercase tracking-wider hover:bg-white transition-colors neon-glow"
              >
                Booking Sekarang <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
